/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { FC, useEffect, useState } from 'react';
import SwaggerUI from 'swagger-ui-react';
import 'swagger-ui-react/swagger-ui.css';
import { Message } from '@domain-group/fe-co-messages';
import * as PortalApi from '../../lib/portal-api';
import * as styles from './swagger.styles';
import { SwaggerContextState } from './swaggerContext';
import * as gtag from '../../lib/gtag';

// Create the layout component
function OperationsLayout(props: {
  getComponent: (arg0: string, arg1: boolean) => any;
}) {
  const { getComponent } = props;
  const Operations = getComponent('operations', true);
  return (
    <div className="swagger-ui">
      <Operations />
    </div>
  );
}

export const PluginOperationsLayout = (): any => ({
  components: {
    OperationsLayout,
  },
});

/**
 * Filter the operations in the given spec to the availableOperationsIds if its is provided
 */
function filterAvailableOperations(
  spec: any,
  availableOperationsIds: string[],
) {
  // eslint-disable-next-line no-restricted-syntax, guard-for-in
  for (const path in spec.paths) {
    // eslint-disable-next-line no-restricted-syntax, guard-for-in
    for (const method in spec.paths[path]) {
      const currentOperation = spec.paths[path][method];
      if (
        !availableOperationsIds.includes(
          currentOperation.operationId.toLowerCase(),
        )
      ) {
        // TODO delete ? other ways ?
        // eslint-disable-next-line no-param-reassign
        delete spec.paths[path][method];
      }
    }
  }
}

interface SwaggerProps {
  version: string;
  operation?: string;
  swaggerContext: SwaggerContextState;
  systemConfig: PortalApi.SystemConfigViewModel;
}

function replaceBaseUrl(originalUrl: string, baseUrl: string) {
  const original = new URL(originalUrl);
  const environmentBaseUrl = new URL(baseUrl);
  original.protocol = environmentBaseUrl.protocol;
  original.hostname = environmentBaseUrl.hostname;

  // Strangely, in google chrome, Setting url.port to '' or null actually sets it to '0'.
  // So the below logic will try to set it to the default port based on the url protocol unless specified.
  if (environmentBaseUrl.port === '') {
    original.port = environmentBaseUrl.protocol === 'https:' ? '443' : '80';
  } else {
    original.port = environmentBaseUrl.port;
  }

  original.pathname = environmentBaseUrl.pathname.endsWith('/')
    ? environmentBaseUrl.pathname + original.pathname.substring(1)
    : environmentBaseUrl.pathname + original.pathname;

  return original.href;
}

function authFn(
  req: any,
  context: SwaggerContextState,
  config: PortalApi.SystemConfigViewModel,
): any {
  if (req.loadSpec) {
    return req;
  }
  // Set appropriate auth token
  if (!context.isAuthenticated) {
    const error = 'You must authenticate a credential first.';
    // eslint-disable-next-line no-alert
    alert(error);
    throw error;
  }

  if (context.credentialType === 'oAuthClient') {
    req.headers.Authorization = `Bearer ${context.accessToken}`;
  } else if (context.credentialType === 'apiKey') {
    req.headers['X-Api-Key'] = `${context.accessToken}`;
  }

  // Indicator for logging that this api call came from the live-api
  req.headers['X-Api-Call-Source'] = 'live-api-browser';

  const projectEnvironment = context.projectEnvironment ?? 'primary';
  const environmentBaseUrl =
    projectEnvironment === 'sandbox'
      ? config.liveDocsApiEndPoints.sandbox
      : config.liveDocsApiEndPoints.primary;
  // set appropriate base url
  // console.log(`update base url of ${req.url} to ${environmentBaseUrl} for ${projectEnvironment}`)
  // req.url = "http://localhost:5001/v1/agencies/4"
  req.url = replaceBaseUrl(req.url, environmentBaseUrl);

  gtag.event({
    category: 'docs-live-ui',
    action: 'execute',
    label: req.url,
  });

  return req;
}

const Swagger: FC<SwaggerProps> = ({
  version,
  operation,
  swaggerContext,
  systemConfig,
}) => {
  const singleOperationMode = operation || false;

  const [swaggerSpec, updateSwaggerSpec] = useState<any>({});
  useEffect(() => {
    // load swagger ui here...

    const cacheBuster = Math.floor(Date.now() / (1000 * 3600 * 1));

    fetch(`/static/latest/media/${version}/openapi.json?cb=${cacheBuster}`)
      .then((response) => response.json())
      .then((spec) => {
        if (!swaggerSpec.spec?.openapi) {
          // to allow filtering by tagged operation Id, copy all the operationIds into the Tags
          // filterAvailableOperations(spec, [operation])
          if (singleOperationMode && operation) {
            filterAvailableOperations(spec, [operation]);
          } else if (!singleOperationMode) {
            filterAvailableOperations(spec, swaggerContext.availableOperations);
          }
          updateSwaggerSpec({
            spec,
          });
        }
      });
  }, [
    version,
    operation,
    swaggerSpec,
    singleOperationMode,
    swaggerContext.availableOperations,
  ]);
  return (
    <>
      {singleOperationMode &&
      operation &&
      swaggerContext.projectId &&
      swaggerContext.availableOperations.indexOf(operation) < 0 ? (
        <Message
          type="error"
          headline="The chosen project does not have access to call this endpoint"
          detail="Choose a different project, or add the required API package to this project."
        />
      ) : (
        <></>
      )}
      <div
        css={
          singleOperationMode
            ? styles.singleOperationSwagger
            : styles.multiOperationSwagger
        }
      >
        {swaggerSpec.spec ? (
          <SwaggerUI
            spec={swaggerSpec.spec}
            displayOperationId={false}
            defaultModelExpandDepth={1}
            showExtensions={false}
            plugins={[PluginOperationsLayout()]}
            layout="OperationsLayout"
            docExpansion={singleOperationMode ? 'full' : 'list'}
            showMutatedRequest
            requestInterceptor={(req: any) =>
              authFn(req, swaggerContext, systemConfig)
            }
          />
        ) : (
          <p>OpenAPI Spec has not been downloaded.</p>
        )}
      </div>
    </>
  );
};

export default Swagger;
